// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// -------------------------------------------------------------------------------
// Component, selectors and others
// -------------------------------------------------------------------------------
import { Paragraph11 } from "themes/default/_typography";
import { PlanCard } from "components/Common/Cards/PlanCard";
import { BasicCard } from "components/Common/Cards/BasicCard";
import { LoadingMask } from "components/Common/LoadingMask";
import ContactSalesModal from "components/Modals/ContactSalesModal";
import { GearIcon } from "components/Icons";
import GymSubscriptionModal from "components/Modals/GymSubscriptionModal";
import SetupPaymentMethodModal from "components/Modals/SetupPaymentMethodModal";

// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import { selectProducts } from "store/selectors";
import { updateSelectedProduct, updateCurrentMembershipDetails, updateSelectedMembership } from "store/slices";
import {
	useGetProductsQuery,
	useGetDomainGymQuery,
	useStripeCreateSubscriptionPriceMutation,
	useStripeUpdateMembershipPricingMutation,
	useStripeUpdateGymSubscriptionMutation,
	useStripeGetGymProductInternalQuery,
	useStripeGetGymSubscriptionQuery,
	useGetPaymentMethodsQuery,
} from "store/api/api";

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import { paymentPlans } from "helpers/constants";
import StyledPlanSelection from "./styles";

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
const PlanSelection = ({ className, ...other }) => {
	const { gym } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const user = useRecoilValue(userAtom);
	const [showContactSalesModal, setShowContactSalesModal] = useState(false);
	const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(false);
	const { data, isLoading } = useGetProductsQuery(selectProducts);
	const domainGymResponse = useGetDomainGymQuery(gym);
	const gymId = domainGymResponse?.data?.id;
	const [createSubscriptionPriceTrigger] = useStripeCreateSubscriptionPriceMutation();
	const [updateMembershipPricingTrigger] = useStripeUpdateMembershipPricingMutation();
	const [updateGymSubscriptionTrigger] = useStripeUpdateGymSubscriptionMutation();
	const { data: paymentMethods, refetch: refetchPaymentMethods } = useGetPaymentMethodsQuery(
		{ customer_id: domainGymResponse?.data?.customer_id },
		{ skip: !domainGymResponse?.data?.customer_id }
	);
	const { data: gymSubscription, refetch: refetchGymSubscription } = useStripeGetGymSubscriptionQuery(
		{ gymId },
		{ skip: !gymId }
	);
	const { data: gymProduct, refetch: refetchGymProduct } = useStripeGetGymProductInternalQuery(
		{ gymId },
		{ skip: !gymId }
	);

	const [subscribing, setSubscribing] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState(null);

	const currentSubscription = gymSubscription?.subscription?.plan?.product?.name;

	useEffect(() => {
		if (selectedProduct && showPaymentMethodModal) {
			handleSelectPlan(selectedProduct);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentMethods]);

	const handleSelectPlan = async (product) => {
		if (
			currentSubscription &&
			product.productName !== paymentPlans.starter.productName &&
			paymentMethods?.length === 0
		) {
			setSelectedProduct(product);
			setShowPaymentMethodModal(true);
			return;
		}
		dispatch(updateSelectedProduct({ product }));
		setShowPaymentMethodModal(false);
		setSubscribing(true);

		const priceInCents = product.minMembersPrice * 100;
		const gymProductPrice =
			gymProduct && !isNaN(Number(gymProduct?.unit_amount)) ? Number(gymProduct.unit_amount) : null;

		try {
			if (!gymProduct) {
				const priceSubscription = await createSubscriptionPriceTrigger({
					account_id: user?.gym?.stripe_info?.stripe_connected_account_id,
					product_name: `Monthly: ${domainGymResponse?.data?.name}`,
					price: priceInCents,
				}).unwrap();

				if (priceSubscription.default_price.unit_amount > 0) {
					dispatch(updateSelectedMembership({ membership: "monthly" }));
				} else {
					dispatch(updateSelectedMembership({ membership: "free" }));
				}

				dispatch(
					updateCurrentMembershipDetails({
						currentMembershipDetails: {
							id: priceSubscription.id,
							product: priceSubscription.id,
							unit_amount: priceSubscription.default_price.unit_amount,
							type: priceSubscription.type,
							membersPay: priceSubscription.default_price.unit_amount / 100,
							default_price: priceSubscription.default_price.id,
						},
					})
				);

				await refetchGymProduct();
			} else if (gymProduct && gymProductPrice < priceInCents) {
				const updatedPrice = await updateMembershipPricingTrigger({
					product_id: gymProduct.id,
					new_price: priceInCents,
				}).unwrap();

				if (updatedPrice?.unit_amount > 0) {
					dispatch(updateSelectedMembership({ membership: "monthly" }));
				} else {
					dispatch(updateSelectedMembership({ membership: "free" }));
				}

				dispatch(
					updateCurrentMembershipDetails({
						currentMembershipDetails: {
							id: updatedPrice?.product,
							product: updatedPrice?.product,
							unit_amount: updatedPrice?.unit_amount,
							type: updatedPrice?.type,
							membersPay: updatedPrice?.unit_amount / 100,
							default_price: updatedPrice?.id,
						},
					})
				);

				await refetchGymProduct();
			}

			if (currentSubscription) {
				await updateGymSubscriptionTrigger({
					gym_id: gymId,
					product_name: product.productName,
				});
				await refetchGymSubscription();

				setSubscribing(false);
				navigate(`/${gym}/dashboard/pricing-plan`);
			} else {
				setSelectedPlan(product);
				setSubscribing(false);
			}
		} catch (err) {
			console.error("Error in plan selection:", err);
			setSubscribing(false);
		}
	};

	return (
		<StyledPlanSelection className={`${className} sb-plan-selection`} {...other}>
			<div className="sb-plan-selection__plans-container">
				{isLoading || subscribing ? (
					<LoadingMask />
				) : (
					<>
						{data?.products?.map((product) =>
							paymentPlans[product.type] ? (
								<PlanCard
									isCurrentSubscription={currentSubscription === paymentPlans[product.type].productName}
									type={product.type}
									onClick={() =>
										handleSelectPlan({
											...product,
											productName: paymentPlans[product.type].productName,
											minMembersPrice: paymentPlans[product.type].minMembersPrice,
										})
									}
									key={paymentPlans[product.type].title}
									icon={paymentPlans[product.type].icon}
									title={paymentPlans[product.type].title}
									price={paymentPlans[product.type].price}
									priceId={paymentPlans[product.type].price_id}
									priceDescription={paymentPlans[product.type].priceDescription}
									description={paymentPlans[product.type].description}
									featuresList={paymentPlans[product.type].featuresList}
									priceFrequency={paymentPlans[product.type].priceFrequency}
								/>
							) : null
						)}
						<PlanCard
							type="elite"
							onClick={() => setShowContactSalesModal(true)}
							key={paymentPlans["elite"].title}
							icon={paymentPlans["elite"].icon}
							title={paymentPlans["elite"].title}
							price={paymentPlans["elite"].price}
							priceDescription={paymentPlans["elite"].priceDescription}
							description={paymentPlans["elite"].description}
							featuresList={paymentPlans["elite"].featuresList}
							priceFrequency={paymentPlans["elite"].priceFrequency}
						/>
					</>
				)}
			</div>
			<div className="sb-plan-selection__disclaimer">
				<BasicCard icon={<GearIcon />}>
					<Paragraph11>
						Pro and Pro Studio tiers are designed for gyms and studios and allow you to include SWEATBASE as part of
						your membership, or charge separately. You can decide to charge as much or as little as you wish. These
						tiers include a bundle of song plays. These plays do not roll over from month to month and your account will
						be charged at $.03 per song per user once the included plays are exceeded.
					</Paragraph11>
				</BasicCard>
			</div>
			<ContactSalesModal open={showContactSalesModal} setOpen={setShowContactSalesModal} />
			<GymSubscriptionModal
				open={!!selectedPlan && !currentSubscription}
				onClose={() => setSelectedPlan(null)}
				onComplete={() => {
					refetchGymSubscription();
					refetchGymProduct();
					navigate(`/${gym}/dashboard/pricing-plan`);
				}}
				gymId={domainGymResponse?.data?.id}
				productName={selectedPlan?.productName}
			/>
			<SetupPaymentMethodModal
				open={showPaymentMethodModal}
				onClose={() => {
					setShowPaymentMethodModal(false);
					setSelectedPlan(null);
					setSelectedProduct(null);
				}}
				type="gym"
				customerId={domainGymResponse?.data?.customer_id}
				afterComplete={() => {
					refetchPaymentMethods();
				}}
			/>
		</StyledPlanSelection>
	);
};

export default PlanSelection;
